import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { CurrencyEnum } from 'common/types/CurrencyType'
import { currencyFormat } from 'common/utils/currency-format'
import { css, RuleSet } from 'styled-components'
import {
  GROUP_TITLE,
  HIGH_DENOMINATION_LOCALES,
  PRICE_GROUP_IDS,
  PRICE_PLAN_TITLE,
  UNLIMITED_THRESHOLD,
  UNLIMITED_VARIANTS_THRESHOLD,
  WEBSITE_EXCLUDED_INTEGRATIONS,
  WEBSITE_EXCLUDED_PRICE_GROUPS,
} from '../const/price-element-consts'
import {
  PriceElementGroupInterface,
  PriceElementPeriodInterface,
  PricePlanPeriodType,
  UserPlansResponseInterface,
} from '../price-element-interface'

export const isHighDenominationLocale = (locale: PageLocaleEnum) =>
  HIGH_DENOMINATION_LOCALES.includes(locale)

export const getPriceGroupDefault = (
  planCount = 4,
): PriceElementGroupInterface[] => {
  const getUnknownArray = () => Array(planCount).fill('unknown')

  return [
    {
      id: PRICE_GROUP_IDS.contacts,
      icon: 'contactsAndCrm',
      title: GROUP_TITLE.contacts,
      items: [
        {
          title: PRICE_PLAN_TITLE.contacts,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.tags,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.crmPipelines,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.emailMarketing,
      icon: 'funnel',
      title: GROUP_TITLE.emailMarketing,
      items: [
        {
          title: PRICE_PLAN_TITLE.emailsSent,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.emailCampaigns,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.emailNewsletters,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.automationAndWorkflows,
      icon: 'automationsAndWorkflows',
      title: GROUP_TITLE.automationAndWorkflows,
      items: [
        {
          title: PRICE_PLAN_TITLE.automationRules,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.workflows,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.sales,
      icon: 'funnel',
      title: GROUP_TITLE.sales,
      items: [
        {
          title: PRICE_PLAN_TITLE.salesFunnels,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.salesFunnelsSteps,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.deadlineFunnels,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.evergreenWebinars,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.tests,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.customDomains,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.fileStorageSpace,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.clickUpsells,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.orderBumps,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.website,
      icon: 'website',
      title: GROUP_TITLE.website,
      items: [
        {
          title: PRICE_PLAN_TITLE.websites,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.websiteLocales,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.subaccounts,
      icon: 'subaccounts',
      title: GROUP_TITLE.subaccounts,
      tooltip: `components.price_element.tooltip.subaccounts`,
      items: [
        {
          title: PRICE_PLAN_TITLE.subaccounts,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.booking,
      icon: 'booking',
      title: GROUP_TITLE.booking,
      items: [
        {
          title: PRICE_PLAN_TITLE.events,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.bookings,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.payment,
      icon: 'payment',
      title: GROUP_TITLE.payment,
      items: [
        {
          title: PRICE_PLAN_TITLE.stripe,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.applePay,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.paypal,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.razorpay,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.flutterwave,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.mercadoPago,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.xendit,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.cash,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.blogs,
      icon: 'blogs',
      title: GROUP_TITLE.blogs,
      items: [
        {
          title: PRICE_PLAN_TITLE.blogs,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.blogPosts,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.link,
      icon: 'link',
      title: GROUP_TITLE.link,
      items: [
        {
          title: PRICE_PLAN_TITLE.mobileLink,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.mobileStore,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.eCommerce,
      icon: 'e-commerce',
      title: GROUP_TITLE.eCommerce,
      items: [
        {
          title: PRICE_PLAN_TITLE.physicalProducts,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.shippingManagement,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.inventoryManagement,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.cash,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.variantsPerProduct,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.couponCodes,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.courses,
      icon: 'courses',
      title: GROUP_TITLE.courses,
      items: [
        {
          title: PRICE_PLAN_TITLE.courses,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.students,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.studentsActivityHistory,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.communities,
      icon: 'communities',
      title: GROUP_TITLE.communities,
      items: [
        {
          title: PRICE_PLAN_TITLE.communities,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.communityMembers,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.affiliate,
      icon: 'affiliate',
      title: GROUP_TITLE.affiliate,
      items: [
        {
          title: PRICE_PLAN_TITLE.affiliateProgram,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.support,
      icon: 'support',
      title: GROUP_TITLE.support,
      items: [
        {
          title: PRICE_PLAN_TITLE.support,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.kickstart,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.team,
      icon: 'team',
      title: GROUP_TITLE.team,
      items: [
        {
          title: PRICE_PLAN_TITLE.assistantAccounts,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.smsMarketing,
      icon: 'sms_marketing',
      title: GROUP_TITLE.smsMarketing,
      items: [
        {
          title: PRICE_PLAN_TITLE.sms,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.integrations,
      icon: 'integrations',
      title: GROUP_TITLE.integrations,
      items: [
        {
          title: PRICE_PLAN_TITLE.googleSheetsIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.googleCalendarIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.googleMeetIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.zoomIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.activeCampaignIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.sendGridIntegration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.twilioSms,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.api,
          plans: getUnknownArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.perks,
      icon: 'perks',
      title: GROUP_TITLE.perks,
      items: [
        {
          title: PRICE_PLAN_TITLE.freeMigration,
          plans: getUnknownArray(),
        },
        {
          title: PRICE_PLAN_TITLE.earlyAccess,
          plans: getUnknownArray(),
        },
      ],
    },
  ]
}

export const getPriceGroup = (
  period: PricePlanPeriodType,
  plans: UserPlansResponseInterface[],
  isWebsitePage: boolean = false,
): PriceElementGroupInterface[] => {
  if (!plans.length) {
    return getPriceGroupDefault()
  }

  const planCount = plans.length

  const getPlan = (
    number: number,
    unlimitedThreshold = UNLIMITED_THRESHOLD,
  ): string => {
    return number >= unlimitedThreshold
      ? 'Unlimited'
      : new Intl.NumberFormat('en-IN').format(number)
  }

  const getUnlimitedArray = () => Array(planCount).fill('Unlimited')
  const getBooleanArray = (value: boolean) => Array(planCount).fill(value)
  const getZeroPercentArray = () => Array(planCount).fill('0%')

  const getProgressiveArray = (values: (string | boolean)[]) =>
    values.slice(0, planCount)

  const isAnnually = period === 'annually'

  let commonPriceGroups: PriceElementGroupInterface[] = [
    {
      id: PRICE_GROUP_IDS.contacts,
      icon: 'contactsAndCrm',
      title: GROUP_TITLE.contacts,
      items: [
        {
          title: PRICE_PLAN_TITLE.contacts,
          plans: plans.map(el => getPlan(el.maxContacts)),
        },
        {
          title: PRICE_PLAN_TITLE.tags,
          plans: plans.map(el => getPlan(el.maxTags)),
        },
        {
          title: PRICE_PLAN_TITLE.crmPipelines,
          plans: plans.map(el => getPlan(el.maxCrmPipelines)),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.emailMarketing,
      icon: 'funnel',
      title: GROUP_TITLE.emailMarketing,
      items: [
        {
          title: PRICE_PLAN_TITLE.emailsSent,
          plans: getUnlimitedArray(),
        },
        {
          title: PRICE_PLAN_TITLE.emailCampaigns,
          plans: plans.map(el => getPlan(el.maxCampaigns)),
        },
        {
          title: PRICE_PLAN_TITLE.emailNewsletters,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.automationAndWorkflows,
      icon: 'automationsAndWorkflows',
      title: GROUP_TITLE.automationAndWorkflows,
      items: [
        {
          title: PRICE_PLAN_TITLE.automationRules,
          plans: plans.map(el => getPlan(el.maxAutomationRules)),
        },
        {
          title: PRICE_PLAN_TITLE.workflows,
          plans: plans.map(el => getPlan(el.maxWorkflows)),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.sales,
      icon: 'funnel',
      title: GROUP_TITLE.sales,
      items: [
        {
          title: PRICE_PLAN_TITLE.salesFunnels,
          plans: plans.map(el => getPlan(el.maxFunnels)),
        },
        {
          title: PRICE_PLAN_TITLE.salesFunnelsSteps,
          plans: plans.map(el => getPlan(el.maxFunnelSteps)),
        },
        {
          title: PRICE_PLAN_TITLE.deadlineFunnels,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.evergreenWebinars,
          plans: plans.map(el => getPlan(el.maxWebinars)),
        },
        {
          title: PRICE_PLAN_TITLE.tests,
          plans: plans.map(el => getPlan(el.maxSplitTests)),
        },
        {
          title: PRICE_PLAN_TITLE.customDomains,
          plans: plans.map(el => getPlan(el.maxUserDomains)),
        },
        {
          title: PRICE_PLAN_TITLE.fileStorageSpace,
          plans: getUnlimitedArray(),
        },
        {
          title: PRICE_PLAN_TITLE.clickUpsells,
          plans: plans.map(el => getPlan(el.maxUpsells)),
        },
        {
          title: PRICE_PLAN_TITLE.orderBumps,
          plans: plans.map(el => getPlan(el.maxOrderBumps)),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getZeroPercentArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.website,
      icon: 'website',
      title: GROUP_TITLE.website,
      items: [
        {
          title: PRICE_PLAN_TITLE.websites,
          plans: plans.map(el => getPlan(el.maxWebsites)),
        },
        {
          title: PRICE_PLAN_TITLE.websiteLocales,
          plans: plans.map(el => getPlan(el.maxWebsiteLocales)),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.subaccounts,
      icon: 'subaccounts',
      title: GROUP_TITLE.subaccounts,
      tooltip: `components.price_element.tooltip.subaccounts`,
      items: [
        {
          title: PRICE_PLAN_TITLE.subaccounts,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.booking,
      icon: 'booking',
      title: GROUP_TITLE.booking,
      items: [
        {
          title: PRICE_PLAN_TITLE.events,
          plans: plans.map(el => {
            return getPlan(el.maxBookingEvents)
          }),
        },
        {
          title: PRICE_PLAN_TITLE.bookings,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.payment,
      icon: 'payment',
      title: GROUP_TITLE.payment,
      items: [
        { title: PRICE_PLAN_TITLE.stripe, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.applePay, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.paypal, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.razorpay, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.flutterwave,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.mercadoPago,
          plans: getBooleanArray(true),
        },
        { title: PRICE_PLAN_TITLE.xendit, plans: getBooleanArray(true) },
        { title: PRICE_PLAN_TITLE.cash, plans: getBooleanArray(true) },
      ],
    },
    {
      id: PRICE_GROUP_IDS.blogs,
      icon: 'blogs',
      title: GROUP_TITLE.blogs,
      items: [
        {
          title: PRICE_PLAN_TITLE.blogs,
          plans: plans.map(el => getPlan(el.maxBlogs)),
        },
        {
          title: PRICE_PLAN_TITLE.blogPosts,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.link,
      icon: 'link',
      title: GROUP_TITLE.link,
      items: [
        { title: PRICE_PLAN_TITLE.mobileLink, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.mobileStore,
          plans: getProgressiveArray(['1', '3', '10', 'Unlimited']),
        },
        {
          title: PRICE_PLAN_TITLE.transactionFees,
          plans: getZeroPercentArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.eCommerce,
      icon: 'e-commerce',
      title: GROUP_TITLE.eCommerce,
      items: [
        {
          title: PRICE_PLAN_TITLE.physicalProducts,
          plans: getUnlimitedArray(),
        },
        {
          title: PRICE_PLAN_TITLE.shippingManagement,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.inventoryManagement,
          plans: getBooleanArray(true),
        },
        { title: PRICE_PLAN_TITLE.cash, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.variantsPerProduct,
          plans: plans.map(el =>
            getPlan(el.maxProductVariants, UNLIMITED_VARIANTS_THRESHOLD),
          ),
        },
        {
          title: PRICE_PLAN_TITLE.couponCodes,
          plans: plans.map(el => getPlan(el.maxCoupons)),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.courses,
      icon: 'courses',
      title: GROUP_TITLE.courses,
      items: [
        {
          title: PRICE_PLAN_TITLE.courses,
          plans: plans.map(el => getPlan(el.maxMembershipCourses)),
        },
        {
          title: PRICE_PLAN_TITLE.students,
          plans: getProgressiveArray([
            '500',
            'Unlimited',
            'Unlimited',
            'Unlimited',
          ]),
        },
        {
          title: PRICE_PLAN_TITLE.studentsActivityHistory,
          plans: getBooleanArray(true),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.communities,
      icon: 'communities',
      title: GROUP_TITLE.communities,
      items: [
        {
          title: PRICE_PLAN_TITLE.communities,
          plans: plans.map(el => getPlan(el.maxCommunities)),
        },
        {
          title: PRICE_PLAN_TITLE.communityMembers,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.affiliate,
      icon: 'affiliate',
      title: GROUP_TITLE.affiliate,
      items: [
        {
          title: PRICE_PLAN_TITLE.affiliateProgram,
          plans: getBooleanArray(true),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.support,
      icon: 'support',
      title: GROUP_TITLE.support,
      items: [
        { title: PRICE_PLAN_TITLE.support, plans: getBooleanArray(true) },
        {
          title: PRICE_PLAN_TITLE.kickstart,
          plans: getProgressiveArray([false, isAnnually, isAnnually, true]),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.team,
      icon: 'team',
      title: GROUP_TITLE.team,
      items: [
        {
          title: PRICE_PLAN_TITLE.assistantAccounts,
          plans: getUnlimitedArray(),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.smsMarketing,
      icon: 'sms_marketing',
      title: GROUP_TITLE.smsMarketing,
      items: [
        {
          title: PRICE_PLAN_TITLE.sms,
          plans: getBooleanArray(true),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.integrations,
      icon: 'integrations',
      title: GROUP_TITLE.integrations,
      items: [
        {
          title: PRICE_PLAN_TITLE.googleSheetsIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.googleCalendarIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.googleMeetIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.zoomIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.activeCampaignIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.sendGridIntegration,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.twilioSms,
          plans: getBooleanArray(true),
        },
        {
          title: PRICE_PLAN_TITLE.api,
          plans: getProgressiveArray(['30', '60', '120', 'Unlimited']),
        },
      ],
    },
    {
      id: PRICE_GROUP_IDS.perks,
      icon: 'perks',
      title: GROUP_TITLE.perks,
      items: [
        {
          title: PRICE_PLAN_TITLE.freeMigration,
          plans: getProgressiveArray([false, isAnnually, isAnnually, true]),
        },
        {
          title: PRICE_PLAN_TITLE.earlyAccess,
          plans: getProgressiveArray([false, false, false, true]),
        },
      ],
    },
  ]
  if (isWebsitePage) {
    commonPriceGroups = commonPriceGroups
      .filter(({ id }) => !WEBSITE_EXCLUDED_PRICE_GROUPS.includes(id))
      .map(group => {
        if (group.id === PRICE_GROUP_IDS.integrations) {
          return {
            ...group,
            items: group.items.filter(
              item => !WEBSITE_EXCLUDED_INTEGRATIONS.includes(item.title),
            ),
          }
        }
        return group
      })
  }

  return commonPriceGroups
}

export const getDefaultPricePeriod = (
  count: number,
  period: PricePlanPeriodType = 'monthly',
): PriceElementPeriodInterface[] =>
  [...Array(count).keys()].map(el => ({
    id: el,
    title: null,
    cost: null,
    currency: null,
    isFree: false,
    period:
      period === 'monthly'
        ? 'components.price_element.period.month'
        : 'components.price_element.period.year',
  }))

export const getPricePeriod = (
  response: UserPlansResponseInterface[],
  btnType: PricePlanPeriodType = 'monthly',
  locale: PageLocaleEnum = PageLocaleEnum.ENGLISH,
): PriceElementPeriodInterface[] =>
  response.map(el => {
    const cost = btnType === 'monthly' ? el.monthCost : el.annualCost
    const period =
      btnType === 'monthly'
        ? 'components.price_element.period.month'
        : 'components.price_element.period.year'
    return {
      id: el.id,
      title: el.title,
      currency: el.currency,
      cost: currencyFormat(
        cost / 100,
        el.currency || CurrencyEnum.EURO,
        locale,
        true,
        'narrowSymbol',
      ),
      isFree: cost === 0,
      period,
    }
  })

export const getScrollSnapStyles = (): RuleSet => css`
  width: 50%;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
`

export const getPriceFormatter = (id: number) => {
  switch (id) {
    case 40:
      return 'components.price_element.freemium_description'
    case 53:
      return 'components.price_element.startup_description'
    case 43:
      return 'components.price_element.webinar_description'
    case 44:
      return 'components.price_element.unlimited_description'
    default:
      return ''
  }
}
